// Sign in with Apple, the only way in. Notices explain why the user is here after deleting the
// account or when a session ended.

import { useState } from "react";
import { Notice, StateCard } from "../components/Controls.tsx";
import { Shell } from "../components/Shell.tsx";
import { supabase } from "../lib/supabase.ts";

const appStoreURL = "https://apps.apple.com/us/app/vehicle-log-book-carveny/id6816715564";

const notices: Record<string, { tone: "info" | "warning"; text: string }> = {
  deleted: { tone: "info", text: "Your online account is deleted. Trips on your iPhone and in iCloud weren’t touched." },
  "deleted-revoke-failed": {
    tone: "warning",
    text: "Your online account is deleted. To stop Sign in with Apple for Carveny too, open Settings on your iPhone, tap your name, then Sign-In & Security › Sign in with Apple › Carveny.",
  },
  "session-ended": { tone: "info", text: "You were signed out. Sign in again to pick up where you left off." },
  "signed-out": { tone: "info", text: "You’re signed out of this browser. Your trips stay online and keep backing up from your iPhone." },
};

export function SignIn() {
  const notice = notices[new URLSearchParams(window.location.search).get("notice") ?? ""];
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  async function signInWithApple() {
    setError(null);
    setIsRedirecting(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: { redirectTo: `${window.location.origin}/trips`, scopes: "email" },
    });
    if (error) {
      setIsRedirecting(false);
      setError("Sign in with Apple couldn’t start. Check your connection and try again.");
    }
  }

  return (
    <Shell section={null} lastUpload={undefined}>
      {notice && (
        <div style={{ maxWidth: 560, margin: "8px auto 0" }}>
          <Notice tone={notice.tone}>{notice.text}</Notice>
        </div>
      )}
      <StateCard icon="cloudUp" title="Carveny Online" id="sign-in-title">
        <p>Your Carveny trips on any computer. Classify them, check your totals and download your mileage log.</p>
        <div style={{ marginTop: 26 }}>
          <button className="cv-btn cv-btn-apple" type="button" onClick={signInWithApple} disabled={isRedirecting}>
            <span className="apple" aria-hidden="true"></span>Sign in with Apple
          </button>
          <p className="cv-hint" style={{ marginTop: 10 }}>Use the Apple ID you use in Carveny on your iPhone.</p>
          {error && <p role="alert" style={{ color: "var(--danger)", marginTop: 10 }}>{error}</p>}
        </div>
        <div className="cv-notice is-info" style={{ marginTop: 24, textAlign: "left" }}>
          <p><b>New to Carveny Online?</b> Turn it on in the app first: Trips › Trip Settings › Carveny Online.</p>
        </div>
        <div className="cv-getapp">
          <img src="/app-store-qr.svg" width="88" height="88" alt="QR code for Carveny on the App Store" />
          <p><b>Don’t have Carveny yet?</b><br />Scan the code with your iPhone camera, or <a href={appStoreURL}>get it on the App Store</a>.</p>
        </div>
      </StateCard>
    </Shell>
  );
}
